import React, { useEffect } from 'react';

import { Button, Grid } from '@sunwisesoftware/sunwise-ui';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import { ShowErrors } from 'common/components';
import {
    ReactHookFormInput,
    ReactHookFormSelect,
} from 'common/components/form/RHF';
import { TEMPLATE_TYPES } from 'common/constants';
import { getSystemTypesOptions } from 'common/utils/helpers';
import yupResolver from 'common/utils/yupResolver';

import * as actions from '../actions';
import { getLanguageOptions, getTypeOptions } from '../helpers';
import * as selectors from '../selectors';
import validate from '../validate';

const TemplateForm = ({
    closeModal,
    errors,
    initialValues,
    isNewRecord,
    isSaving,
    save,
    systemTypes,
}) => {
    const { t } = useTranslation();

    const { control, handleSubmit, reset, watch } = useForm({
        defaultValues: initialValues,
        resolver: yupResolver(validate),
    });

    useEffect(() => {
        reset(initialValues);
    }, [initialValues]);

    const handleClickSave = (values) => save(values);

    return (
        <form>
            <Grid container>
                <Grid size={18}>
                    <ReactHookFormInput
                        control={control}
                        label={t('Name').concat(' *')}
                        name="title"
                    />
                </Grid>

                <Grid size={18}>
                    <ReactHookFormSelect
                        autoComplete="off"
                        control={control}
                        disabled={!isNewRecord}
                        id="language"
                        label={t('Language').concat('*')}
                        name="language"
                        options={getLanguageOptions()}
                    />
                </Grid>

                <Grid size={18}>
                    <ReactHookFormSelect
                        autoComplete="off"
                        control={control}
                        id="type"
                        label={t('Type').concat('*')}
                        name="type"
                        options={getTypeOptions()}
                    />
                </Grid>

                {[TEMPLATE_TYPES.BOM_TYPE, TEMPLATE_TYPES.SYSTEM_TYPE].includes(
                    watch('type'),
                ) && (
                    <Grid size={18}>
                        <ReactHookFormSelect
                            autoComplete="off"
                            control={control}
                            label={t('System type').concat(' *')}
                            name="system"
                            options={getSystemTypesOptions(systemTypes)}
                        />
                    </Grid>
                )}
            </Grid>

            <ShowErrors
                errors={errors}
                mt={2}
                severity="error"
                variant="filled"
            />

            <Grid container spacing={2} mt={2} justifyContent="flex-end">
                <Grid>
                    <Button onClick={() => closeModal()} variant="outlined">
                        {t('Cancel')}
                    </Button>
                </Grid>
                <Grid>
                    <Button
                        disabled={isSaving}
                        onClick={handleSubmit(handleClickSave)}
                    >
                        {isSaving ? `${t('Saving')}...` : t('Save')}
                    </Button>
                </Grid>
            </Grid>
        </form>
    );
};

TemplateForm.propTypes = {
    closeModal: PropTypes.func,
    errors: PropTypes.array,
    initialValues: PropTypes.object,
    isNewRecord: PropTypes.bool,
    isSaving: PropTypes.bool,
    save: PropTypes.func,
    systemTypes: PropTypes.array,
};

const mapStateToProps = createStructuredSelector({
    errors: selectors.getErrorsSave,
    initialValues: selectors.getInitialValues,
    isSaving: selectors.getIsSaving,
});

const mapDispatchToProps = (dispatch) => ({
    save: (values) => dispatch(actions.save(values)),
});

export default connect(mapStateToProps, mapDispatchToProps)(TemplateForm);
